import type React from 'react'
import { useContext, useState } from 'react'
import styled from 'styled-components'

import { Button } from '@/components/DEPRECATED_UI/Button'
import LoadingSpinner from '@/components/LoadingSpinner'
import ctx from '@/context/AppContext'

import TextInput from './TextInput'

interface Props {
  address: Address
  onChangeAddress: (address: Address) => void
}

export interface Address {
  equifaxId: string
  bedroomIdentifier: string
  name: string
  line1: string
  line2: string
  city: string
  postcode: string
  country: string
  udprn?: string
  uprn?: string
}

export interface PostcodeSearchResult {
  postcode: string
  postcode_inward: string
  postcode_outward: string
  post_town: string
  dependant_locality: string
  double_dependant_locality: string
  thoroughfare: string
  dependant_thoroughfare: string
  building_number: string
  building_name: string
  sub_building_name: string
  po_box: string
  department_name: string
  organisation_name: string
  udprn: number
  postcode_type: string
  su_organisation_indicator: string
  delivery_point_suffix: string
  line_1: string
  line_2: string
  line_3: string
  premise: string
  longitude: number
  latitude: number
  eastings: number
  northings: number
  country: string
  traditional_county: string
  administrative_county: string
  postal_county: string
  county: string
  district: string
  ward: string
  uprn: string
  id: string
  country_iso: string
  country_iso_2: string
  county_code: string
  language: string
  umprn: string
  dataset: string
}

const PostcodeSearchContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr min-content;
  grid-gap: 10px;
`

const SearchResultsContainer = styled.div`
  max-height: 200px;
  overflow-y: auto;
`

const NoStyleUL = styled.ul`
  margin: 0;
`

const ButtonLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #069;
  text-decoration: underline;
  cursor: pointer;
`

const isEmptyString = (value?: string | null) =>
  value === null || value === undefined || value.length === 0

export const toAddress = (searchResult: PostcodeSearchResult): Address => {
  const name = !isEmptyString(searchResult.premise)
    ? searchResult.premise
    : searchResult.line_1
  const line1 = !isEmptyString(searchResult.thoroughfare)
    ? searchResult.thoroughfare
    : !isEmptyString(searchResult.dependant_locality)
      ? searchResult.dependant_locality
      : searchResult.line_1
  const line2 = [
    searchResult.dependant_thoroughfare,
    searchResult.double_dependant_locality,
  ]
    .filter(part => !isEmptyString(part))
    .join(' ')
    .trim()

  const countryName =
    searchResult.country_iso === 'GBR'
      ? 'United Kingdom of Great Britain and Northern Ireland'
      : searchResult.country

  return {
    equifaxId: '',
    bedroomIdentifier: '',
    name: name,
    line1: line1,
    line2: line2,
    city: searchResult.post_town,
    postcode: searchResult.postcode,
    country: countryName,
    udprn: searchResult.udprn.toString(),
    uprn: searchResult.uprn,
  }
}

const formatAddress = (addr: PostcodeSearchResult): string => {
  const line2 = ((addr.line_2 ?? '') + ' ' + (addr.line_3 ?? '')).trim()

  return [addr.line_1, line2, addr.post_town, addr.country]
    .filter(part => part && part.trim() !== '')
    .join(', ')
}

const AddressSelector: React.FC<Props> = ({ address, onChangeAddress }) => {
  const [searchedPostcode, setSearchedPostcode] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [foundAddresses, setFoundAddresses] = useState<PostcodeSearchResult[]>([])
  const { accessKey } = useContext(ctx)

  const findAddress = async (postcode: string) => {
    setLoading(true)
    setFoundAddresses([])

    const res = await fetch(
      `${import.meta.env.VITE_VOUCH_API}/dashboard/postcode/${postcode}`,
      {
        method: 'POST',
        headers: {
          'Ocp-Apim-Subscription-Key': accessKey,
        },
      },
    )

    if (res.status === 200) {
      const data = await res.json()
      if (data.result && Array.isArray(data.result)) {
        setFoundAddresses(data.result)
      }
    }

    setLoading(false)
  }

  const onEditAddressField =
    (key: string): React.ChangeEventHandler<HTMLInputElement> =>
    e =>
      onChangeAddress({
        ...address,
        equifaxId: '',
        [key]: e.target.value,
      })

  return (
    <>
      <PostcodeSearchContainer>
        <TextInput
          value={searchedPostcode}
          type="text"
          label="Find By Postcode"
          name="usrPostcode"
          onChange={e => setSearchedPostcode(e.target.value)}
        />
        <Button disabled={loading} onClick={() => findAddress(searchedPostcode)}>
          Search
        </Button>
      </PostcodeSearchContainer>
      <SearchResultsContainer>
        {loading ? (
          <LoadingSpinner size="1x" />
        ) : (
          <NoStyleUL>
            {foundAddresses.map((addr, i) => (
              <li key={i}>
                <ButtonLink
                  key={addr.uprn}
                  onClick={() => {
                    onChangeAddress(toAddress(addr))
                    setFoundAddresses([])
                    setSearchedPostcode('')
                  }}
                >
                  {formatAddress(addr)}
                </ButtonLink>
              </li>
            ))}
          </NoStyleUL>
        )}
      </SearchResultsContainer>
      <TextInput
        value={address.bedroomIdentifier}
        onChange={onEditAddressField('bedroomIdentifier')}
        type="text"
        label="Bedroom Identifier"
        name="bedroomIdentifier"
      />
      <TextInput
        value={address.name}
        onChange={onEditAddressField('name')}
        type="text"
        label="Name/Number"
        name="name"
      />
      <TextInput
        value={address.line1}
        onChange={onEditAddressField('line1')}
        type="text"
        label="Line 1"
        name="line1"
      />
      <TextInput
        value={address.line2}
        onChange={onEditAddressField('line2')}
        type="text"
        label="Line 2"
        name="line2"
      />
      <TextInput
        value={address.city}
        onChange={onEditAddressField('city')}
        type="text"
        label="City"
        name="city"
      />
      <TextInput
        value={address.postcode}
        onChange={onEditAddressField('postcode')}
        type="text"
        label="Postcode"
        name="postcode"
      />
      <TextInput
        value={address.country}
        onChange={onEditAddressField('country')}
        type="text"
        label="Country"
        name="country"
      />
    </>
  )
}

export default AddressSelector
